/**
 * AddQuestionsModal Component
 * Modal for generating additional questions for Express/Standard interviews.
 *
 * For Express Interview (1 skill): No skill selection needed
 * For Standard Interview (3 skills): User selects which skill to add questions for
 *
 * Options:
 * - Core Conceptual: AI generates fundamental concepts questions
 * - Practical: AI generates hands-on usage questions
 * - Scenario Based: AI generates realistic scenario questions
 * - Custom Prompt: AI generates based on user's custom prompt
 * - Create Question: User manually creates a question with up to 5 key points
 * - Question Base: Browse cached questions by skill + years of experience
 */

import { useState, useEffect } from 'react'
import {
  XMarkIcon,
  LightBulbIcon,
  WrenchScrewdriverIcon,
  PuzzlePieceIcon,
  PencilSquareIcon,
  SparklesIcon,
  ArrowLeftIcon,
  PlusIcon,
  TrashIcon,
  CircleStackIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline'
import { toast } from 'react-hot-toast'
import { aiInterviewsApi } from '../../services/api'
import type { AIInterviewType, QuestionCategory, AdditionalQuestion } from '../../types/interview'

interface AddQuestionsModalProps {
  isOpen: boolean
  onClose: () => void
  interviewId: string
  interviewType: AIInterviewType
  selectedSkills: string[]
  onQuestionsGenerated: (questions: AdditionalQuestion[]) => void
  onGeneratingChange?: (isGenerating: boolean) => void
}

type OptionType = 'core_conceptual' | 'practical' | 'scenario_based' | 'custom_prompt' | 'create_question' | 'question_base'

interface CategoryOption {
  id: OptionType
  name: string
  description: string
  icon: React.ComponentType<{ className?: string }>
  color: string
}

const CATEGORIES: CategoryOption[] = [
  {
    id: 'core_conceptual',
    name: 'Core Conceptual',
    description: 'AI generates fundamental concepts, principles, definitions',
    icon: LightBulbIcon,
    color: 'blue',
  },
  {
    id: 'practical',
    name: 'Practical',
    description: 'AI generates hands-on usage, implementation questions',
    icon: WrenchScrewdriverIcon,
    color: 'green',
  },
  {
    id: 'scenario_based',
    name: 'Scenario Based',
    description: 'AI generates realistic scenarios, problem-solving questions',
    icon: PuzzlePieceIcon,
    color: 'purple',
  },
  {
    id: 'custom_prompt',
    name: 'Custom Prompt',
    description: 'Provide your own prompt for AI question generation',
    icon: SparklesIcon,
    color: 'indigo',
  },
  {
    id: 'create_question',
    name: 'Create Question',
    description: 'Manually create a question with up to 5 key points',
    icon: PencilSquareIcon,
    color: 'orange',
  },
  {
    id: 'question_base',
    name: 'Question Base',
    description: 'Browse cached questions by years of experience',
    icon: CircleStackIcon,
    color: 'teal',
  },
]

interface CachedQuestion {
  question: string
  skill?: string
  skill_aspect_tested?: string
  difficulty_level?: string
  expected_answer?: { key_points: string[] }
  key_evaluation_points?: string[]
}

export default function AddQuestionsModal({
  isOpen,
  onClose,
  interviewId,
  interviewType,
  selectedSkills,
  onQuestionsGenerated,
  onGeneratingChange,
}: AddQuestionsModalProps) {
  const [step, setStep] = useState<
    'skill' | 'category' | 'custom_prompt' | 'create_question' | 'generating' |
    'question_base_years' | 'question_base_results'
  >('skill')
  const [selectedSkill, setSelectedSkill] = useState<string | null>(null)
  const [selectedOption, setSelectedOption] = useState<OptionType | null>(null)
  const [customPrompt, setCustomPrompt] = useState('')
  const [isGenerating, setIsGenerating] = useState(false)

  // Manual question creation state
  const [manualQuestion, setManualQuestion] = useState('')
  const [manualAspect, setManualAspect] = useState('')
  const [keyPoints, setKeyPoints] = useState<string[]>([''])

  // Question Base state
  const [qbYears, setQbYears] = useState<number>(5)
  const [qbLoading, setQbLoading] = useState(false)
  const [qbFound, setQbFound] = useState(false)
  const [qbQuestions, setQbQuestions] = useState<CachedQuestion[]>([])
  const [qbSelected, setQbSelected] = useState<Set<number>>(new Set())

  // For Express interview with single skill, skip skill selection
  const shouldSkipSkillSelection = interviewType === 'express' && selectedSkills.length === 1

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      if (shouldSkipSkillSelection) {
        setSelectedSkill(selectedSkills[0])
        setStep('category')
      } else {
        setSelectedSkill(null)
        setStep('skill')
      }
      setSelectedOption(null)
      setCustomPrompt('')
      setIsGenerating(false)
      setManualQuestion('')
      setManualAspect('')
      setKeyPoints([''])
      setQbYears(5)
      setQbLoading(false)
      setQbFound(false)
      setQbQuestions([])
      setQbSelected(new Set())
    }
  }, [isOpen, shouldSkipSkillSelection, selectedSkills])

  if (!isOpen) return null

  const handleSkillSelect = (skill: string) => {
    setSelectedSkill(skill)
    setStep('category')
  }

  const handleOptionSelect = (option: OptionType) => {
    setSelectedOption(option)
    if (option === 'custom_prompt') {
      setStep('custom_prompt')
    } else if (option === 'create_question') {
      setStep('create_question')
    } else if (option === 'question_base') {
      setQbYears(5)
      setQbQuestions([])
      setQbSelected(new Set())
      setStep('question_base_years')
    } else {
      handleGenerateQuestions(option as QuestionCategory)
    }
  }

  const handleGenerateQuestions = async (category?: QuestionCategory, prompt?: string) => {
    if (!selectedSkill) {
      toast.error('Please select a skill first')
      return
    }

    setIsGenerating(true)
    onGeneratingChange?.(true)
    setStep('generating')

    try {
      const requestData: {
        skill: string
        category?: 'core_conceptual' | 'practical' | 'scenario_based'
        custom_prompt?: string
      } = {
        skill: selectedSkill,
      }

      if (prompt) {
        requestData.custom_prompt = prompt
      } else if (category && category !== 'custom') {
        requestData.category = category
      }

      const response = await aiInterviewsApi.generateAdditionalQuestions(interviewId, requestData)
      const data = response.data as {
        status: string
        questions: AdditionalQuestion[]
        question_count: number
        error?: string
      }

      if (data.status === 'success' && data.questions.length > 0) {
        toast.success(`Generated ${data.question_count} additional questions`)
        onQuestionsGenerated(data.questions)
        onClose()
      } else {
        toast.error(data.error || 'Failed to generate questions')
        setStep('category')
      }
    } catch (error: any) {
      console.error('Failed to generate additional questions:', error)
      const message = error?.response?.data?.detail || 'Failed to generate questions'
      toast.error(message)
      setStep('category')
    } finally {
      setIsGenerating(false)
      onGeneratingChange?.(false)
    }
  }

  // Question Base handlers
  const handleQbSearch = async () => {
    if (!selectedSkill) return

    setQbLoading(true)
    setQbQuestions([])
    setQbSelected(new Set())

    try {
      const response = await aiInterviewsApi.getCachedQuestions(selectedSkill, qbYears)
      const data = response.data as {
        found: boolean
        questions: CachedQuestion[]
        question_count: number
      }

      setQbFound(data.found)
      setQbQuestions(data.questions)
      setStep('question_base_results')
    } catch (error: any) {
      toast.error('Failed to search question base')
    } finally {
      setQbLoading(false)
    }
  }

  const handleQbToggle = (index: number) => {
    setQbSelected(prev => {
      const next = new Set(prev)
      if (next.has(index)) next.delete(index)
      else next.add(index)
      return next
    })
  }

  const handleQbSelectAll = () => {
    if (qbSelected.size === qbQuestions.length) {
      setQbSelected(new Set())
    } else {
      setQbSelected(new Set(qbQuestions.map((_, i) => i)))
    }
  }

  const handleQbAddSelected = () => {
    if (qbSelected.size === 0) {
      toast.error('Select at least one question')
      return
    }

    const questions: AdditionalQuestion[] = Array.from(qbSelected).map(i => {
      const q = qbQuestions[i]
      const keyPts = q.expected_answer?.key_points || q.key_evaluation_points || []
      return {
        question: q.question,
        skill: selectedSkill!,
        skill_aspect_tested: q.skill_aspect_tested,
        category: 'question_base',
        expected_answer: keyPts.length > 0 ? { key_points: keyPts } : undefined,
        is_additional: true,
      }
    })

    toast.success(`Added ${questions.length} question${questions.length > 1 ? 's' : ''} from Question Base`)
    onQuestionsGenerated(questions)
    onClose()
  }

  const handleAddKeyPoint = () => {
    if (keyPoints.length < 5) {
      setKeyPoints([...keyPoints, ''])
    }
  }

  const handleRemoveKeyPoint = (index: number) => {
    if (keyPoints.length > 1) {
      setKeyPoints(keyPoints.filter((_, i) => i !== index))
    }
  }

  const handleKeyPointChange = (index: number, value: string) => {
    const updated = [...keyPoints]
    updated[index] = value
    setKeyPoints(updated)
  }

  const handleCreateManualQuestion = () => {
    if (!selectedSkill) {
      toast.error('Please select a skill first')
      return
    }

    if (!manualQuestion.trim()) {
      toast.error('Please enter a question')
      return
    }

    const validKeyPoints = keyPoints.filter(kp => kp.trim())

    const newQuestion: AdditionalQuestion = {
      question: manualQuestion.trim(),
      skill: selectedSkill,
      skill_aspect_tested: manualAspect.trim() || undefined,
      category: 'custom',
      expected_answer: validKeyPoints.length > 0 ? { key_points: validKeyPoints } : undefined,
      is_additional: true,
    }

    toast.success('Question created successfully')
    onQuestionsGenerated([newQuestion])
    onClose()
  }

  const handleBack = () => {
    if (step === 'custom_prompt' || step === 'create_question' || step === 'question_base_years') {
      setStep('category')
      setSelectedOption(null)
    } else if (step === 'question_base_results') {
      setStep('question_base_years')
    } else if (step === 'category' && !shouldSkipSkillSelection) {
      setStep('skill')
      setSelectedSkill(null)
    }
  }

  const canGoBack = (step === 'category' && !shouldSkipSkillSelection) ||
    step === 'custom_prompt' ||
    step === 'create_question' ||
    step === 'question_base_years' ||
    step === 'question_base_results'

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 overflow-y-auto h-full w-full z-[60] flex items-center justify-center">
      <div className="relative w-full max-w-lg mx-4 bg-white rounded-2xl shadow-xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 flex-shrink-0">
          <div className="flex items-center">
            {canGoBack && (
              <button
                onClick={handleBack}
                className="mr-3 p-1 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <ArrowLeftIcon className="h-5 w-5" />
              </button>
            )}
            <SparklesIcon className="h-5 w-5 text-blue-600 mr-2" />
            <h3 className="text-lg font-medium text-gray-900">Add Questions</h3>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto flex-1">

          {/* Step: Skill Selection (for Standard interviews) */}
          {step === 'skill' && !shouldSkipSkillSelection && (
            <div>
              <p className="text-sm text-gray-500 mb-4">
                Select a skill to add questions for:
              </p>
              <div className="space-y-2">
                {selectedSkills.map((skill) => (
                  <button
                    key={skill}
                    onClick={() => handleSkillSelect(skill)}
                    className="w-full px-4 py-3 text-left rounded-lg border border-gray-200 hover:border-blue-500 hover:bg-blue-50 transition-colors"
                  >
                    <span className="font-medium text-gray-900">{skill}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Step: Category/Option Selection */}
          {step === 'category' && (
            <div>
              <div className="flex items-center justify-between mb-4">
                <p className="text-sm text-gray-500">
                  {selectedSkill && (
                    <span>
                      Adding questions for <span className="font-medium text-gray-900">{selectedSkill}</span>
                    </span>
                  )}
                </p>
              </div>
              <p className="text-sm text-gray-500 mb-4">
                Choose how to add questions:
              </p>
              <div className="space-y-2">
                {CATEGORIES.map((category) => {
                  const Icon = category.icon
                  const colorClasses: Record<string, string> = {
                    blue: 'hover:border-blue-500 hover:bg-blue-50',
                    green: 'hover:border-green-500 hover:bg-green-50',
                    purple: 'hover:border-purple-500 hover:bg-purple-50',
                    indigo: 'hover:border-indigo-500 hover:bg-indigo-50',
                    orange: 'hover:border-orange-500 hover:bg-orange-50',
                    teal: 'hover:border-teal-500 hover:bg-teal-50',
                  }
                  const iconColorClasses: Record<string, string> = {
                    blue: 'text-blue-600',
                    green: 'text-green-600',
                    purple: 'text-purple-600',
                    indigo: 'text-indigo-600',
                    orange: 'text-orange-600',
                    teal: 'text-teal-600',
                  }
                  return (
                    <button
                      key={category.id}
                      onClick={() => handleOptionSelect(category.id)}
                      className={`w-full px-4 py-3 text-left rounded-lg border border-gray-200 transition-colors ${colorClasses[category.color]}`}
                    >
                      <div className="flex items-center">
                        <Icon className={`h-5 w-5 mr-3 flex-shrink-0 ${iconColorClasses[category.color]}`} />
                        <div>
                          <span className="font-medium text-gray-900">{category.name}</span>
                          <p className="text-xs text-gray-500 mt-0.5">{category.description}</p>
                        </div>
                      </div>
                    </button>
                  )
                })}
              </div>
            </div>
          )}

          {/* Step: Custom Prompt */}
          {step === 'custom_prompt' && (
            <div>
              <p className="text-sm text-gray-500 mb-2">
                Custom AI generation for <span className="font-medium text-gray-900">{selectedSkill}</span>
              </p>
              <p className="text-sm text-gray-500 mb-3">
                Describe what kind of questions you want the AI to generate:
              </p>
              <textarea
                value={customPrompt}
                onChange={(e) => setCustomPrompt(e.target.value)}
                placeholder="e.g., Focus on advanced topics like performance optimization, memory management, and best practices for large-scale applications..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                rows={4}
              />
              <button
                onClick={() => handleGenerateQuestions(undefined, customPrompt)}
                disabled={!customPrompt.trim() || isGenerating}
                className="mt-4 w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Generate Questions with AI
              </button>
            </div>
          )}

          {/* Step: Create Manual Question */}
          {step === 'create_question' && (
            <div>
              <p className="text-sm text-gray-500 mb-4">
                Create question for <span className="font-medium text-gray-900">{selectedSkill}</span>
              </p>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Question <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={manualQuestion}
                  onChange={(e) => setManualQuestion(e.target.value)}
                  placeholder="Enter your interview question..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  rows={3}
                />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Skill Aspect Tested <span className="text-gray-400">(optional)</span>
                </label>
                <input
                  type="text"
                  value={manualAspect}
                  onChange={(e) => setManualAspect(e.target.value)}
                  placeholder="e.g., Problem diagnosis and error handling"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                />
              </div>

              <div className="mb-4">
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Key Evaluation Points <span className="text-gray-400">(max 5)</span>
                  </label>
                  {keyPoints.length < 5 && (
                    <button
                      type="button"
                      onClick={handleAddKeyPoint}
                      className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                    >
                      <PlusIcon className="h-4 w-4 mr-1" />
                      Add Point
                    </button>
                  )}
                </div>
                <div className="space-y-2">
                  {keyPoints.map((point, index) => (
                    <div key={index} className="flex items-center space-x-2">
                      <span className="text-sm text-gray-500 w-6">{index + 1}.</span>
                      <input
                        type="text"
                        value={point}
                        onChange={(e) => handleKeyPointChange(index, e.target.value)}
                        placeholder={`Key point ${index + 1}...`}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                      />
                      {keyPoints.length > 1 && (
                        <button
                          type="button"
                          onClick={() => handleRemoveKeyPoint(index)}
                          className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  Key points help the interviewer evaluate candidate responses.
                </p>
              </div>

              <button
                onClick={handleCreateManualQuestion}
                disabled={!manualQuestion.trim()}
                className="w-full px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Create Question
              </button>
            </div>
          )}

          {/* Step: Generating */}
          {step === 'generating' && (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-4 text-sm text-gray-600">
                Generating questions for <span className="font-medium">{selectedSkill}</span>...
              </p>
              <p className="mt-1 text-xs text-gray-500">
                This may take a moment
              </p>
            </div>
          )}

          {/* Step: Question Base — Enter Years */}
          {step === 'question_base_years' && (
            <div>
              <div className="flex items-center gap-2 mb-1">
                <CircleStackIcon className="h-5 w-5 text-teal-600" />
                <p className="text-sm font-medium text-gray-900">
                  Question Base — <span className="text-teal-700">{selectedSkill}</span>
                </p>
              </div>
              <p className="text-sm text-gray-500 mb-6">
                Enter the candidate's years of experience. We'll find matching questions from the question base.
              </p>

              <label className="block text-sm font-medium text-gray-700 mb-2">
                Years of Experience
              </label>
              <div className="flex items-center gap-3 mb-6">
                <button
                  type="button"
                  onClick={() => setQbYears(v => Math.max(0, v - 1))}
                  className="w-9 h-9 rounded-full border border-gray-300 flex items-center justify-center text-gray-600 hover:bg-gray-100 transition-colors text-lg leading-none"
                >
                  −
                </button>
                <input
                  type="number"
                  min={0}
                  max={50}
                  value={qbYears}
                  onChange={(e) => setQbYears(Math.max(0, Math.min(50, parseInt(e.target.value) || 0)))}
                  className="w-20 text-center border border-gray-300 rounded-lg px-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
                <button
                  type="button"
                  onClick={() => setQbYears(v => Math.min(50, v + 1))}
                  className="w-9 h-9 rounded-full border border-gray-300 flex items-center justify-center text-gray-600 hover:bg-gray-100 transition-colors text-lg leading-none"
                >
                  +
                </button>
                <span className="text-sm text-gray-500">yr{qbYears !== 1 ? 's' : ''}</span>
              </div>

              <div className="bg-teal-50 border border-teal-200 rounded-lg p-3 mb-6">
                <p className="text-xs text-teal-700">
                  <span className="font-medium">Fuzzy match:</span>{' '}
                  {qbYears <= 10
                    ? `±2 years (searching ${Math.max(0, qbYears - 2)}–${qbYears + 2} yrs)`
                    : qbYears <= 20
                    ? `±3 years (searching ${Math.max(0, qbYears - 3)}–${qbYears + 3} yrs)`
                    : `±4 years (searching ${Math.max(0, qbYears - 4)}–${qbYears + 4} yrs)`}
                </p>
              </div>

              <button
                onClick={handleQbSearch}
                disabled={qbLoading}
                className="w-full px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
              >
                {qbLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                    Searching...
                  </>
                ) : (
                  <>
                    <CircleStackIcon className="h-4 w-4" />
                    Search Question Base
                  </>
                )}
              </button>
            </div>
          )}

          {/* Step: Question Base — Results */}
          {step === 'question_base_results' && (
            <div>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <p className="text-sm font-medium text-gray-900">
                    {qbFound ? (
                      <span>{qbQuestions.length} question{qbQuestions.length !== 1 ? 's' : ''} found</span>
                    ) : (
                      <span className="text-gray-500">No questions found</span>
                    )}
                  </p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {selectedSkill} · {qbYears} yr{qbYears !== 1 ? 's' : ''} experience
                  </p>
                </div>
                {qbFound && qbQuestions.length > 0 && (
                  <button
                    onClick={handleQbSelectAll}
                    className="text-xs text-teal-600 hover:text-teal-800 font-medium"
                  >
                    {qbSelected.size === qbQuestions.length ? 'Deselect All' : 'Select All'}
                  </button>
                )}
              </div>

              {!qbFound || qbQuestions.length === 0 ? (
                <div className="text-center py-8 bg-gray-50 rounded-lg">
                  <CircleStackIcon className="mx-auto h-10 w-10 text-gray-300 mb-3" />
                  <p className="text-sm text-gray-500">
                    No cached questions found for <strong>{selectedSkill}</strong> at {qbYears} years.
                  </p>
                  <p className="text-xs text-gray-400 mt-1">
                    Try a different year range or use AI generation instead.
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  {qbQuestions.map((q, i) => (
                    <button
                      key={i}
                      onClick={() => handleQbToggle(i)}
                      className={`w-full text-left px-4 py-3 rounded-lg border transition-all ${
                        qbSelected.has(i)
                          ? 'border-teal-500 bg-teal-50'
                          : 'border-gray-200 hover:border-teal-300 hover:bg-teal-50/40'
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`mt-0.5 flex-shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${
                          qbSelected.has(i)
                            ? 'border-teal-500 bg-teal-500'
                            : 'border-gray-300'
                        }`}>
                          {qbSelected.has(i) && (
                            <CheckCircleIcon className="w-4 h-4 text-white" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-gray-800 leading-snug">{q.question}</p>
                          {q.skill_aspect_tested && (
                            <p className="text-xs text-gray-400 mt-1">{q.skill_aspect_tested}</p>
                          )}
                          {q.difficulty_level && (
                            <span className="inline-block mt-1 text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">
                              {q.difficulty_level}
                            </span>
                          )}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer — only for Question Base results */}
        {step === 'question_base_results' && qbFound && qbQuestions.length > 0 && (
          <div className="flex-shrink-0 px-6 py-4 border-t border-gray-200 bg-gray-50">
            <button
              onClick={handleQbAddSelected}
              disabled={qbSelected.size === 0}
              className="w-full px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
            >
              {qbSelected.size === 0
                ? 'Select questions to add'
                : `Add ${qbSelected.size} Question${qbSelected.size > 1 ? 's' : ''}`}
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
